/** Shared display formatters (Redesign v3, UI_SDD §R). */

/** An uploaded folder (DEC-180). Its address names a folder, not a place to fetch it. */
export function isUploadScan(url: string | null | undefined): boolean {
  return !!url && url.startsWith('upload://')
}

/** "https://github.com/org/repo" → "org/repo"; "local:///a/b/repo" → "…/b/repo";
 *  "upload://my repo" → "my repo" */
export function repoLabel(url: string): string {
  // Before URL parsing: a folder name may hold spaces, which `new URL` rejects.
  if (isUploadScan(url)) return url.slice('upload://'.length)
  if (url.startsWith('local://')) {
    const path = url.slice('local://'.length)
    const parts = path.split('/').filter(Boolean)
    return parts.length >= 2 ? `…/${parts.slice(-2).join('/')}` : path
  }
  try {
    const parts = new URL(url).pathname.split('/').filter(Boolean)
    return parts.slice(0, 2).join('/') || url
  } catch {
    return url
  }
}

/** 1234567 → "1.2M", 45300 → "45.3k", 950 → "950" */
export function compactNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return String(n)
}

/** seconds → "1m 04s" | "47s" */
export function durationLabel(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${m}m ${String(s).padStart(2, '0')}s`
}

/** 33206.4 → "$33,206" */
export function dollars(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`
}

/** ISO timestamp → "Jul 13, 2026 · 18:07" (evaluation dates must be visible). */
export function dateTimeLabel(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })
  return `${date} · ${time}`
}
